import { includesContents, numbersToWords } from "@/common/answers";
import {
  API_URL_POST_ONLINE_QUOTE,
  API_URL_POST_QUOTE,
} from "@/constants/api.constants";
import { PRODUCT } from "@/constants/common.constants";
import { AMOUNT_DEFAULT_FORMAT } from "@/constants/currency-format.constants";
import {
  ERROR_ALREADY_HAS_AN_APPLICATION_REQUEST,
  ERROR_ALREADY_HAS_AN_APPLICATION_SESSION,
  ERROR_MSG_UNKNOWN_ERROR,
  ERROR_MSG_WRONG_BANK_DETAILS,
} from "@/constants/message.constants";
import router from "@/router";
import documentService from "@/services/document.service";
import { QuestionStore } from "@/store/store";
import numeral from "numeral";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { CAPITALIZE_LETTER_LIST_ITEMS_REGEX } from "@/constants/regex-patterns.constants";
import { logErrorsToGoogleAnalytics } from "@/common/log-errors";

export const updateHelpshiftConfig = (user) => {
  window.helpshiftConfig.userId = user ? user.userId : null;
  window.helpshiftConfig.userName = user
    ? "HJO - User " + user.userId
    : "HJO User";
  window.Helpshift("updateHelpshiftConfig");
};

export function getPostUrlByQuoteAccess(url, config) {
  if (config.headers.QuoteAccessId) {
    config.withCredentials = true;
    return API_URL_POST_ONLINE_QUOTE + "/" + url;
  } else {
    return API_URL_POST_QUOTE + "/" + url;
  }
}
/**
 * format number to 2 digit format
 * @param {string} day
 */
export function formatTwoDigitNumber(day) {
  if (day == null || day === "") {
    return "";
  }
  return day.length < 2 ? "0" + day : day;
}

export const currencyMask = createNumberMask({
  prefix: "",
  includeThousandsSeparator: true,
  allowDecimal: true,
  allowLeadingZeroes: true,
});

export function addScriptHotjar(hotjarId) {
  var hotjar_script = document.getElementById("hotjar_script");
  var hotjar_script_inner = document.getElementById("hotjar_script_inner");
  if (hotjar_script && hotjar_script_inner) {
    hotjar_script.remove();
    hotjar_script_inner.remove();
  }
  var script = document.createElement("script");
  script.setAttribute("id", "hotjar_script");
  script.textContent =
    "(function(h,o,t,j,a,r){" +
    "h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};" +
    "h._hjSettings={hjid:" +
    hotjarId +
    ",hjsv:6};" +
    "a=o.getElementsByTagName('head')[0];" +
    "r=o.createElement('script');r.setAttribute('id', 'hotjar_script_inner');r.async=1;" +
    "r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;" +
    "a.appendChild(r);" +
    " })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=')";
  document.head.appendChild(script);
}

export function addScriptHelpshift(helpshiftId) {
  const helpshift = document.getElementById("hs-chat");
  if (helpshift) {
    return;
  }

  const script = document.createElement("script");

  script.textContent = `
    (function () {
          var PLATFORM_ID = \`${helpshiftId}\`;
          DOMAIN = "paymentshield",
              LANGUAGE = "en";
          window.helpshiftConfig = {
              platformId: PLATFORM_ID,
              domain: DOMAIN,
              language: LANGUAGE
          };
    })();
    !function (t, e) { if ("function" != typeof window.Helpshift) { var n = function () { n.q.push(arguments) }; n.q = [], window.Helpshift = n; var i, a = t.getElementsByTagName("script")[0]; if (t.getElementById(e)) return; i = t.createElement("script"), i.async = !0, i.id = e, i.src = "https://webchat.helpshift.com/latest/webChat.js";var o = function () { window.Helpshift("init") }; window.attachEvent ? i.attachEvent("onload", o) : i.addEventListener("load", o, !1), a.parentNode.insertBefore(i, a) } else window.Helpshift("update") }(document, "hs-chat");
  `;
  document.head.appendChild(script);
}

export function compareTwoArrayOfString(a, b) {
  return (
    a.length == b.length &&
    a.every((element, index) => {
      return element === b[index];
    })
  );
}

/**
 * change hotjar and helpshift id
 * @param {boolean} isSelfServe whether this quote is a d2c quote
 * @param {number} userId
 */
export function applyThirdPartyId(isSelfServe, userId) {
  if (isSelfServe) {
    applyD2CThirdPartyId();
  } else {
    applyB2BThirdPartyId(userId);
  }
}

const platformIdMap = {
  [PRODUCT.LandLord]: process.env.VUE_APP_PLATFORM_ID_D2C_LJO,
  [PRODUCT.Home]: process.env.VUE_APP_PLATFORM_ID_D2C_HJO, // default platform id
};

/**
 * change helpshift id base on productId for d2c journey
 */
export function addD2CScriptHelpshift() {
  const productId = QuestionStore.getters("productId");
  const platformId = productId
    ? platformIdMap[productId] ?? platformIdMap[PRODUCT.Home]
    : platformIdMap[PRODUCT.Home];
  addScriptHelpshift(platformId);
}

export function applyD2CThirdPartyId() {
  addD2CScriptHelpshift();
}

export function applyB2BThirdPartyId(userId) {
  addScriptHelpshift(process.env.VUE_APP_PLATFORM_ID);
  updateHelpshiftConfig({ userId: userId });
}

/**
 * Add field label to all rules in the object
 * @param {object} rules
 * @param {string} fieldLabel
 * @returns
 */
export function addFieldLabel(rules, fieldLabel) {
  if (!fieldLabel || !rules) {
    return rules;
  }
  const result = Object.entries(rules).reduce((previous, [ruleName, rule]) => {
    previous[ruleName] = { ...rule, fieldLabel: fieldLabel };
    return previous;
  }, {});
  return result;
}
export function formatCurrency(val, format) {
  return "£" + numeral(val).format(format || AMOUNT_DEFAULT_FORMAT);
}

export function isEmpty(obj) {
  for (const prop in obj) {
    return false;
  }
  return true;
}

export function scrollTop() {
  window.scroll({
    top: 0,
    behavior: "smooth",
  });
}

export function scrollToFirstErrorInput(validationObserver) {
  if (!validationObserver || !validationObserver.refs) {
    return;
  }
  const errors = validationObserver.errors;
  let invalidInputRef;
  for (const [key, value] of Object.entries(errors)) {
    if (value && value.length && value[0]) {
      invalidInputRef = key;
      break;
    }
  }
  if (!invalidInputRef) {
    return;
  }
  const element = validationObserver.refs[invalidInputRef]?.$el;
  if (element == null) {
    return;
  }
  element.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

export function scrollToElement(
  element,
  behavior = "smooth",
  position = "start"
) {
  if (!element) return;
  element.scrollIntoView({
    behavior: behavior,
    block: position,
  });
}

export function convertToBool(value) {
  if (!value) {
    return false;
  }
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return !!value;
}

export function getErrorMessage(caughtError) {
  const responseData = caughtError?.response?.data;

  setTimeout(() => {
    let elm = document.querySelector(".error-message");
    elm?.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  }, 300);

  if (
    responseData?.StatusCode === "ClientError" &&
    responseData.Messages?.length &&
    responseData.Messages[0]?.Items?.length >= 2
  ) {
    const serverErrorMsg = responseData.Messages[0]?.Items[1];
    const matchedError = ERROR_MSG_WRONG_BANK_DETAILS.find((error) => {
      if (
        serverErrorMsg.includes(error.errorCode) ||
        serverErrorMsg.includes(error.errorMsg)
      ) {
        return error;
      }
    });

    if (matchedError) {
      return matchedError.message;
    } else {
      logErrorsToGoogleAnalytics(
        responseData.Messages[0]?.Items,
        QuestionStore.getters("productId")
      );
      return responseData.Messages[0]?.Items;
    }
  } else if (responseData?.StatusCode === "ClientError") {
    if (
      [
        ERROR_ALREADY_HAS_AN_APPLICATION_REQUEST,
        ERROR_ALREADY_HAS_AN_APPLICATION_SESSION,
      ].includes(responseData?.Messages[0]?.Summary)
    ) {
      return responseData?.Messages[0]?.Summary + ".";
    } else {
      logErrorsToGoogleAnalytics(
        responseData.Messages[0]?.Items,
        QuestionStore.getters("productId")
      );
      return responseData.Messages[0]?.Items;
    }
  } else if (responseData?.Summary) {
    return responseData.Summary;
  } else {
    return ERROR_MSG_UNKNOWN_ERROR;
  }
}

/**
 *
 * @param {String} content
 * @param {String} documentName
 */
export const downloadDocument = (content, documentName = "Doc") => {
  const href = window.URL.createObjectURL(content);
  const anchorElement = document.createElement("a");
  anchorElement.href = href;
  anchorElement.download = documentName;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  document.body.removeChild(anchorElement);
  window.URL.revokeObjectURL(href);
};

/**
 * Reset eligibility for get quote
 * @param {Object} form
 */
export function resetEligibilityCheck(form) {
  if (!form.rentGuarantee.Value && !form.legalExpenses.Value) {
    form.addonEligibility.Value = null;
  }
}

/**
 * Convert cover detail data and add to question map
 * @param {Object} form
 * @param {Object} questionMap
 */
export const prepareDataForGetQuote = (form, questionMap) => {
  questionMap.ExcessBuildings.Value = numbersToWords(
    form.excessBuildings.Value
  );
  const includeContentsCover = includesContents(questionMap.CoverType);
  if (includeContentsCover) {
    questionMap.LandlordContentsLevel.Value = numbersToWords(
      form.landlordContentsLevel.Value
    );
    questionMap.ExcessContents.Value = numbersToWords(
      form.excessContents.Value
    );
    questionMap.AccidentalDamageContents.Value = convertToBool(
      form.accidentalDamageContents.Value
    );
  } else {
    questionMap.LandlordContentsLevel.Value = null;
    questionMap.ExcessContents.Value = null;
    questionMap.AccidentalDamageContents.Value = null;
  }
};

/**
 * Reset all invalid input in the observer
 * @param {import("vee-validate").ValidationObserver} observer Reference to the ValidationObserver in the form
 * @returns
 */
export const resetVeeValidateInvalidInput = (observer) => {
  if (!observer.errors || !observer.refs) {
    return;
  }

  for (const [key, ref] of Object.entries(observer.refs)) {
    // reset fields if error is present or input doesn't have value
    if (observer.errors[key]?.length > 0 || !ref.value) {
      ref.reset();
    }
  }
};

/**
 * Tries to go to the next step based on the validity of the form.
 * @param {import("vee-validate").ValidationObserver} form - The form object.
 * @param {(form)=>Promise<boolean>} validateFormFunc - The function used to validate the form.
 * @param {string} routeName - The name of the route to navigate to.
 * @returns {Promise<void>} - A promise that resolves when the navigation is complete.
 */
export const tryGoToNextStep = async (form, validateFormFunc, routeName) => {
  try {
    const validForm = await validateFormFunc(form);
    if (validForm) {
      router.push({
        name: routeName,
      });
      return;
    }
    resetVeeValidateInvalidInput(form);
    QuestionStore.dispatch("setRestoringLastStep", false);
  } catch (e) {
    console.error(e);
    QuestionStore.dispatch("setRestoringLastStep", false);
  }
};

export function checkAndRemoveSessionStorage(key) {
  const value = sessionStorage.getItem(key);
  if (value) {
    sessionStorage.removeItem(key);
  }
}

export const getLatestStaticDocuments = async (productId, initData) => {
  let documents = [...initData];
  try {
    const response = await documentService.getStaticDocuments(productId);
    if (response?.Documents?.length) {
      documents.forEach((doc) => {
        let docData = response?.Documents?.findLast(
          (item) => item.Code === doc.code
        );
        if (docData) {
          doc.Link = docData.Link;
        }
      });
    }
  } catch (error) {
    console.log(error);
  }
  return documents;
};

export const truncateString = (value, start, end) => {
  return value.slice(start, end);
};

export const adobeTargetTriggerView = (path) => {
  let viewName = path === "/" || !path ? "home" : path;

  // Sanitize viewName to get rid of any trailing symbols derived from URL
  if (viewName.startsWith("#") || viewName.startsWith("/")) {
    viewName = viewName.substr(1);
  }

  if (
    typeof window.adobe != "undefined" &&
    window.adobe.target &&
    typeof window.adobe.target.triggerView === "function"
  ) {
    window.adobe.target.triggerView(viewName);
  }
};

export const handleValidationError = () => {
  // Focus on the first input field with an error
  const firstErrorInput = document.querySelector(`.v-messages.error--text`);
  if (firstErrorInput) {
    firstErrorInput.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
};

/**
 * remove trailing last decimal number caused by float representation
 * @param {Number} number float to round
 * @returns rounded float
 */
export const roundFloat = (number) => {
  return Math.round((number + Number.EPSILON) * 100) / 100;
};

/**
 * Format the display text: capitalize the first letter of the sentence and the word after "/" or "/" with n*space
 * @param string
 * @returns formatted string
 */
export const capitalizeLetterText = (text = "") =>
  text
    ?.toLowerCase()
    .replace(CAPITALIZE_LETTER_LIST_ITEMS_REGEX, (match) =>
      match.toUpperCase()
    );

export const sanitizeCurrencyValue = (value) => {
  if (!value) return "0";
  return parseFloat(value.toString().replace(/,/g, "")).toString();
};
